import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { alpha, styled } from '@mui/material/styles';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { basicSort, getUpdatedSortConfig } from '../../utils/misc';
import { Loader } from '../feedback';

const SORT_DIRECTION = {
  ASC: 'asc',
  DESC: 'desc',
};

const ALIGN = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
};

const DEFAULT_SORT_CONFIG = { key: null, direction: SORT_DIRECTION.ASC };

const DEFAULT_EMPTY_MESSAGE = 'No data available';
const SORT_ICON = 'ri:arrow-drop-down-line';
const TABLE_MAX_HEIGHT = '50vh';

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const resolveRowKey = (row, rowIdx, idKey) => row?.[idKey] ?? row?.id ?? `row-${rowIdx}`;

const resolveCellContent = (column, value, row) => {
  if (column.renderCell) return column.renderCell(value, row);
  if (value === null || value === undefined)
    return <span className='unavailable-message'>Not Available</span>;
  return value;
};

/**
 * @typedef {Object} TableColumn
 * @property {string} id - Unique column identifier.
 * @property {string} dataKey - Key used to read row data.
 * @property {string} label - Header label.
 * @property {'left'|'center'|'right'} [align='center'] - Cell alignment.
 * @property {number} [width] - Column width.
 * @property {boolean} [sortable=false] - Enables sorting.
 * @property {(value:any, row:Object) => React.ReactNode} [renderCell] - Custom cell renderer.
 */

/**
 * @component Table
 *
 * @param {Object} props
 * @param {Array<TableColumn>} props.columns - Column configuration.
 * @param {Array<Object>} props.data - Row data.
 * @param {string} [props.selectedRowId] - Selected row identifier.
 * @param {string} [props.selectedIdKey='id'] - Row key used for selection.
 * @param {boolean} [props.disableSort=false] - Disables all sorting.
 * @param {boolean} [props.loading=false] - Shows loading state.
 * @param {string|React.ReactNode} [props.emptyMessage='No data available'] - Empty state message.
 * @param {string|number} [props.resetSortKey] - Changing this resets sorting state.
 *
 * @returns {JSX.Element|null}
 */
export default function Table(props: any) {
  const {
    columns: rawColumns,
    data: rawData,
    selectedRowId,
    selectedIdKey = 'id',
    disableSort = false,
    emptyMessage = DEFAULT_EMPTY_MESSAGE,
    loading = false,
    resetSortKey,
  } = props;

  const columns = normalizeArray(rawColumns);
  const data = normalizeArray(rawData);

  const [sortConfig, setSortConfig] = useState<{key: string; direction: 'asc'|'desc'}> (DEFAULT_SORT_CONFIG);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    return basicSort([...data], sortConfig.key, sortConfig.direction as 'asc' | 'desc');
  }, [data, sortConfig]);

  useEffect(() => {
    if (resetSortKey !== undefined) {
      setSortConfig(DEFAULT_SORT_CONFIG);
    }
  }, [resetSortKey]);

  if (columns.length === 0) {
    return import.meta.env.MODE === 'development' ? (
      <Alert severity='warning'>No columns defined for CustomTable</Alert>
    ) : null;
  }

  const handleSort = (dataKey) => {
    setSortConfig((prev) => getUpdatedSortConfig(prev as any, dataKey) as any);
  };

  const renderEmptyState = () => (
    <TableRow>
      <StyledTableBodyCell
        {...{ colSpan: columns.length, align: 'center' } as any}
        sx={{ py: 4, '::after': { display: 'none' } }}
      >
        <span className='unavailable-message'>{emptyMessage}</span>
      </StyledTableBodyCell>
    </TableRow>
  );

  const renderDividerRow = () => (
    <TableRow>
      <TableCell {...{} as any} colSpan={columns.length} sx={{ p: 0, border: 'none' }}>
        <div className='divider' />
      </TableCell>
    </TableRow>
  );

  return (
    <TableContainer
      sx={{
        maxHeight: TABLE_MAX_HEIGHT,
        overflow: 'auto',
        scrollbarGutter: 'stable',
        pr: 1,
      }}
    >
      <MuiTable stickyHeader sx={{ tableLayout: 'auto' }}>
        <TableHead>
          <TableRow>
            {columns.map((col, colIdx) => {
              const isSortable = col.sortable && !disableSort && data.length > 1;

              return (
                <StyledTableHeaderCell
                  {...{ key: col.id || col.dataKey || `col-${colIdx}`, align: col.align || ALIGN.CENTER, onClick: () => isSortable && handleSort(col.dataKey), className: isSortable ? 'sortable' : '' } as any}
                >
                  <Stack direction='row' justifyContent='center' alignItems='center'>
                    {col.label}
                    {col.sortable && (
                      <Stack
                        sx={{
                          transform:
                            sortConfig.key === col.dataKey &&
                            sortConfig.direction === SORT_DIRECTION.ASC
                              ? 'rotate(180deg)'
                              : 'none',
                          opacity: sortConfig.key === col.dataKey ? 1 : 0.8,
                        }}
                      >
                        <Icon icon={SORT_ICON} width={24} />
                      </Stack>
                    )}
                  </Stack>
                </StyledTableHeaderCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <StyledSpacer>
              <TableCell {...{} as any} colSpan={columns.length}>
                <Stack alignItems='center' width='100%' mt={4}>
                  <Loader />
                </Stack>
              </TableCell>
            </StyledSpacer>
          ) : sortedData.length === 0 ? (
            renderEmptyState()
          ) : (
            <>
              <StyledSpacer>
                <TableCell {...{} as any} colSpan={columns.length} />
              </StyledSpacer>

              {sortedData.map((row, rowIdx) => {
                const rowKey = resolveRowKey(row, rowIdx, selectedIdKey);
                const isSelected = String(selectedRowId) === String(row?.[selectedIdKey]);

                return (
                  <Fragment key={rowKey}>
                    <StyledBodyRow selected={isSelected}>
                      {columns.map((col, colIdx) => (
                        <StyledTableBodyCell
                          key={col.id || col.dataKey || `col-${colIdx}`}
                          align={col.align || 'center'}
                        >
                          {resolveCellContent(col, row[col.dataKey], row)}{' '}
                        </StyledTableBodyCell>
                      ))}
                    </StyledBodyRow>

                    {rowIdx < sortedData.length - 1 && renderDividerRow()}
                  </Fragment>
                );
              })}
            </>
          )}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
}

// ========================= STYLED COMPONENTS =========================

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.header.main || '#DAE8FF',
  color: theme.palette.text.primary,
  fontWeight: 500,
  fontSize: '0.75rem',
  padding: '8px 24px 8px 12px',
  textAlign: 'center',
  border: 'none',
  userSelect: 'none',
  '&.sortable': { cursor: 'pointer' },
}));

const StyledBodyRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  '&:hover': { backgroundColor: alpha(theme.palette.grey[300], 0.2) },
  '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: alpha(theme.palette.grey[300], 0.2) },
}));

const StyledTableBodyCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.primary,
  padding: '16px 24px 16px 12px',
  border: 'none',
}));

const StyledSpacer = styled(TableRow)(({ theme }) => ({
  height: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  '& td': { padding: 0, border: 0 },
}));
